import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import Modal from '../../components/shared/Modal/Modal';
import Badge from '../../components/shared/Badge/Badge';
import Field from '../../components/shared/Field/Field';
import Loading from '../../components/shared/Loading';
import { ResponseDetail, serviceEvaluations } from '../../services/service-evaluations';
import { EvaluationLimits, EvaluationQuestionType, EvaluationRequestStatus } from '../../util/evaluationContract';
import { formatScore } from '../../util/evaluationFormat';
import { requestStatusMeta } from '../../util/evaluationStatusMeta';
import { formatStockholmDay } from './reviewFormat';
import ReadOnlyStars from './ReadOnlyStars';
import { reportUnexpected } from './reportUnexpected';
import styles from './page-supplier-evaluations.module.scss';

type Props = {
  responseId: string;
  onClose: () => void;
  onHidden: () => void;
  /** false hides "Dölj svar" (a read-only selectMode panel); defaults to true. */
  allowHide?: boolean;
};

type Answer = ResponseDetail['answers'][number];

const LATE_STYLE = requestStatusMeta(EvaluationRequestStatus.Overdue);

const AnswerRow: FC<{ answer: Answer }> = ({ answer }) => {
  const [t] = useTranslation('evaluations');
  const notAnswered = t('response.notAnswered', 'Inte besvarad');

  return (
    <li data-answer="" className={styles.answer}>
      <span className={styles.author}>{answer.text}</span>
      {answer.type === EvaluationQuestionType.Rating ? (
        <span className={styles.question}>
          {answer.skipped && <span className={styles.skipped}>{t('answer.skip', 'Vet ej')}</span>}
          {!answer.skipped && answer.score !== null && (
            <>
              <ReadOnlyStars rating={answer.score} size={14} />
              <span className={`${styles.small} ${styles.muted}`}>
                {t('response.scoreOf', '{{score}} av 5', { score: answer.score })}
              </span>
            </>
          )}
          {!answer.skipped && answer.score === null && (
            <span className={styles.muted}>{notAnswered}</span>
          )}
        </span>
      ) : (
        <p className={`${styles.answerText} ${answer.textAnswer ? styles.quote : styles.muted}`}>
          {answer.textAnswer ?? notAnswered}
        </p>
      )}
      {answer.comment && <p className={styles.quote}>{answer.comment}</p>}
    </li>
  );
};

const ResponseView: FC<{ detail: ResponseDetail }> = ({ detail }) => {
  const [t] = useTranslation('evaluations');

  return (
    <div className={styles.response}>
      <div className={styles.responseHead}>
        <div className={styles.responseSummary}>
          {detail.score !== null && <ReadOnlyStars rating={detail.score} size={16} />}
          <span className={styles.responseAverage}>
            {t('response.average', 'Snitt {{score}}', { score: formatScore(detail.score) })}
          </span>
        </div>
        <div className={`${styles.responseMeta} ${styles.muted}`}>
          {[
            detail.projectName,
            t('response.submittedAt', 'Inskickat {{date}}', {
              date: formatStockholmDay(detail.submittedAt),
            }),
          ]
            .filter(Boolean)
            .join(' · ')}
          {detail.late && (
            <Badge color={LATE_STYLE.color} backgroundColor={LATE_STYLE.backgroundColor}>
              {t('response.late', 'Sen')}
            </Badge>
          )}
        </div>
      </div>
      {detail.hidden && (
        <div className={styles.hiddenNotice}>
          {t('response.hiddenNotice', 'Dolt {{date}}: {{reason}}. Svaret räknas inte i betyget.', {
            date: formatStockholmDay(detail.hidden.at),
            reason: detail.hidden.reason,
          })}
        </div>
      )}
      <ol className={styles.answers}>
        {detail.answers
          .filter(answer => answer.type !== EvaluationQuestionType.Paragraph)
          .map(answer => (
            <AnswerRow key={answer.key} answer={answer} />
          ))}
      </ol>
    </div>
  );
};

const ResponseModal: FC<Props> = ({ responseId, onClose, onHidden, allowHide = true }) => {
  const [t] = useTranslation('evaluations');
  const [detail, setDetail] = useState<ResponseDetail | null>(null);
  const [failed, setFailed] = useState(false);
  const [hiding, setHiding] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    serviceEvaluations
      .getResponse(responseId)
      .then(response => active && setDetail(response))
      .catch(error => {
        reportUnexpected(error);
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [responseId]);

  const trimmedReason = reason.trim();
  const reasonTooLong = trimmedReason.length > EvaluationLimits.HideReasonMaxLength;
  const canConfirm = Boolean(trimmedReason) && !reasonTooLong && !saving;

  const hide = async () => {
    if (!canConfirm) return;
    setSaving(true);
    try {
      await serviceEvaluations.hideResponse(responseId, trimmedReason);
      NotificationManager.success(
        t('response.hide.done', 'Svaret är dolt och räknas inte i betyget.'),
      );
      onHidden();
      onClose();
    } catch (error) {
      reportUnexpected(error);
      setSaving(false);
      NotificationManager.error(t('response.hide.failed', 'Svaret kunde inte döljas.'));
    }
  };

  if (hiding && detail) {
    const stopHiding = () => {
      if (!saving) setHiding(false);
    };
    return (
      <Modal
        show
        setShow={stopHiding}
        title={t('response.hide.title', 'Dölj svaret')}
        cancelButtonText={t('response.hide.cancel', 'Avbryt')}
        onCancelButtonClick={stopHiding}
        buttonInfo={{
          label: t('response.hide.confirm', 'Dölj svar'),
          action: hide,
          disabled: !canConfirm,
          loaderShow: saving,
        }}
      >
        <div className={styles.response}>
          <p className={styles.answerText}>
            {t(
              'response.hide.explainer',
              'Svaret från {{name}} slutar räknas i betyget och dess kommentarer döljs. Det raderas inte.',
              { name: detail.respondent.name || detail.respondent.email },
            )}
          </p>
          <Field
            multiline
            label={t('response.hide.reasonLabel', 'Anledning (krävs)')}
            placeholder={t('response.hide.reasonPlaceholder', 't.ex. Svaret gällde fel leverantör')}
            value={reason}
            onChange={setReason}
            errorMessage={
              reasonTooLong
                ? t('response.hide.reasonTooLong', 'Högst {{max}} tecken.', {
                    max: EvaluationLimits.HideReasonMaxLength,
                  })
                : undefined
            }
          />
        </div>
      </Modal>
    );
  }

  const respondent = detail
    ? [detail.respondent.name || detail.respondent.email, detail.respondent.title]
        .filter(Boolean)
        .join(', ')
    : t('response.title', 'Svar');

  return (
    <Modal
      show
      setShow={onClose}
      isScrollable
      hasCloseButton
      title={respondent}
      cancelButtonText={t('response.close', 'Stäng')}
      onCancelButtonClick={onClose}
      containerBeforeButtons={
        allowHide && detail?.canHide && !detail.hidden ? (
          <button type="button" className={styles.hideAction} onClick={() => setHiding(true)}>
            <VisibilityOffOutlinedIcon style={{ fontSize: 16 }} aria-hidden />
            <span>{t('response.hide.open', 'Dölj svar')}</span>
          </button>
        ) : undefined
      }
    >
      {detail && <ResponseView detail={detail} />}
      {!detail && failed && (
        <p className={styles.loadError} role="alert">
          {t('response.loadFailed', 'Svaret kunde inte hämtas.')}
        </p>
      )}
      {!detail && !failed && <Loading type="inline" />}
    </Modal>
  );
};

export default ResponseModal;
